import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ---- State & Actions ----

type Theme = 'light' | 'dark'

interface ThemeState {
  theme: Theme
  primaryColor: string
  accentColor: string
}

interface ThemeActions {
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
  setPrimaryColor: (color: string) => void
  setAccentColor: (color: string) => void
}

type ThemeStore = ThemeState & ThemeActions

// ---- Helpers ----

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
  // Also toggle the 'dark' class for Tailwind dark mode
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

function applyCSSVariable(name: string, value: string) {
  document.documentElement.style.setProperty(name, value)
}

// ---- Store ----

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, _get) => ({
      // State
      // Default: dark — PlayoNews is a dark-first neon portal.
      theme: 'dark',
      primaryColor: '#dc2626', // red-600 — PlayoNetwork primary
      accentColor: '#06b6d4',  // cyan-500 — PlayoNetwork "tech" accent

      // Toggle between light and dark
      toggleTheme: () => {
        set((state) => {
          const newTheme: Theme = state.theme === 'light' ? 'dark' : 'light'
          applyTheme(newTheme)
          return { theme: newTheme }
        })
      },

      // Set a specific theme
      setTheme: (theme: Theme) => {
        applyTheme(theme)
        set({ theme })
      },

      // Set primary color
      setPrimaryColor: (color: string) => {
        applyCSSVariable('--color-primary', color)
        set({ primaryColor: color })
      },

      // Set accent color
      setAccentColor: (color: string) => {
        applyCSSVariable('--color-accent', color)
        set({ accentColor: color })
      },
    }),
    {
      name: 'playonews-theme',
      // On rehydrate, apply the stored theme to the DOM
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            applyTheme(state.theme)
            applyCSSVariable('--color-primary', state.primaryColor)
            applyCSSVariable('--color-accent', state.accentColor)
          }
        }
      },
    }
  )
)
